import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, KeyboardAvoidingView, Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { TEST_WORKER_URL } from '../runtime/mobile/testWorkerClient';
import { colors, spacing, typography } from '../config/theme';
import { APP_NAME } from '../config/brand';
import { ErrorBanner, Field, PrimaryButton, TextButton } from './ui';

// Pulls the invite token out of whatever the employee pasted: a full link
// (…/invite?t=TOKEN), a bare ?t=TOKEN fragment, or the raw token itself.
const extractToken = (raw) => {
  const value = (raw || '').trim();
  if (!value) return '';
  const match = value.match(/[?&]t=([^&\s]+)/);
  if (match) return decodeURIComponent(match[1]);
  return value.includes('~') ? value : '';
};

export default function InviteAcceptScreen({ route, navigation }) {
  const { acceptInvitation } = useAuth();
  const initialToken = extractToken(route?.params?.t || route?.params?.token || '');

  const [token, setToken] = useState(initialToken);
  const [pasted, setPasted] = useState('');
  const [checking, setChecking] = useState(!!initialToken);
  const [inviteValid, setInviteValid] = useState(null); // null = unknown
  const [form, setForm] = useState({ fullName: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const checkToken = useCallback(async (value) => {
    if (!value) { setInviteValid(false); setChecking(false); return; }
    setChecking(true);
    setError(null);
    try {
      const res = await fetch(`${TEST_WORKER_URL}/invite/check`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token: value }),
      });
      const data = await res.json().catch(() => ({}));
      setInviteValid(data.valid === true);
      if (data.valid && data.fullName) set({ fullName: data.fullName });
    } catch {
      setInviteValid(false);
      setError('Не удалось проверить приглашение. Проверьте соединение.');
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { if (initialToken) checkToken(initialToken); }, [initialToken, checkToken]);

  const usePasted = () => {
    const t = extractToken(pasted);
    if (!t) { setError('Не похоже на ссылку-приглашение'); return; }
    setToken(t);
    checkToken(t);
  };

  const submit = async () => {
    if (!form.fullName.trim() || !form.email.trim() || !form.password) { setError('Заполните все поля'); return; }
    if (form.password.length < 6) { setError('Пароль должен быть не короче 6 символов'); return; }
    setBusy(true);
    setError(null);
    try {
      await acceptInvitation({ token, email: form.email, password: form.password, fullName: form.fullName });
      // On success onAuthStateChanged swaps to the employee view automatically.
    } catch (e) {
      const code = e?.code || '';
      setError(
        code === 'auth/email-already-in-use' ? 'Этот email уже зарегистрирован — войдите под ним'
          : code === 'INVITE_INVALID' ? 'Приглашение недействительно или истекло'
          : code === 'auth/weak-password' ? 'Слишком простой пароль'
          : e?.message || 'Не удалось завершить регистрацию',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.root}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <Text style={styles.title}>Приглашение в «{APP_NAME}»</Text>

        {checking && (
          <View style={styles.centerRow}>
            <ActivityIndicator color={colors.primary} />
            <Text style={styles.sub}>Проверяем приглашение…</Text>
          </View>
        )}

        {!checking && inviteValid === false && (
          <>
            <ErrorBanner message={error || 'Приглашение недействительно или истекло. Попросите администратора прислать новую ссылку.'} />
            <Text style={styles.sub}>Если у вас есть ссылка-приглашение, вставьте её сюда:</Text>
            <Field label="Ссылка-приглашение" value={pasted} onChangeText={setPasted} placeholder="https://…/invite?t=…" autoCapitalize="none" />
            <PrimaryButton title="Проверить" onPress={usePasted} />
          </>
        )}

        {!checking && inviteValid === true && (
          <>
            <Text style={styles.sub}>Создайте аккаунт сотрудника. Доступ к сменам откроет администратор.</Text>
            <ErrorBanner message={error} />
            <Field label="Имя и фамилия" value={form.fullName} onChangeText={(fullName) => set({ fullName })} placeholder="Анна Королёва" />
            <Field label="Email" value={form.email} onChangeText={(email) => set({ email })} placeholder="anna@example.com" keyboardType="email-address" autoCapitalize="none" />
            <Field label="Пароль" value={form.password} onChangeText={(password) => set({ password })} placeholder="минимум 6 символов" secureTextEntry autoCapitalize="none" />
            <View style={{ height: spacing.sm }} />
            <PrimaryButton title="Зарегистрироваться" onPress={submit} loading={busy} />
          </>
        )}

        <View style={styles.loginRow}>
          <Text style={styles.loginText}>Уже есть аккаунт?</Text>
          <TextButton title="Войти" onPress={() => navigation.navigate('Login')} />
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.background },
  content: { padding: spacing.lg, gap: spacing.md, flexGrow: 1, justifyContent: 'center', width: '100%', maxWidth: 440, alignSelf: 'center' },
  title: { ...typography.h1, color: colors.textPrimary },
  sub: { ...typography.body, color: colors.textSecondary, marginBottom: spacing.sm },
  centerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  loginRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm, marginTop: spacing.md },
  loginText: { ...typography.body, color: colors.textMuted },
});
